import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { CheckCircle2, Gift } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useState } from 'react';
import { toast } from 'sonner';
import OnboardingStepIndicator from './OnboardingStepIndicator';

/**
 * Step 2 of onboarding (iter95).
 *
 * A single plain-language confirmation that the manager has been
 * granted a 14-day free trial. No plan selection, no payment method
 * prompt. The `POST /api/users/me/trial-ready-acknowledged` call
 * persists a flag so this screen only ever shows once.
 */
export default function TrialReadyStep({ role = 'manager', onDone }) {
  const { api } = useAuth();
  const [busy, setBusy] = useState(false);

  const handleContinue = async () => {
    setBusy(true);
    try {
      await api.patch('/users/me', { trialReadyAcknowledged: true });
      if (onDone) await onDone();
    } catch {
      toast.error('Could not continue — please try again.');
      setBusy(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-[#F1F5F9] flex items-center justify-center p-4"
      data-testid="trial-ready-step"
    >
      <Card className="w-full max-w-md border-0 shadow-lg">
        <CardContent className="p-8">
          {/* TrialReady is Manager step 3 (setup → legal → trial). */}
          <OnboardingStepIndicator current={3} role={role} />

          <div className="flex items-center gap-3 mb-4">
            <div className="h-11 w-11 bg-emerald-100 rounded-xl flex items-center justify-center shrink-0">
              <Gift className="h-6 w-6 text-emerald-700" />
            </div>
            <h2 className="text-xl font-bold text-slate-900">Your free trial is ready</h2>
          </div>

          <p className="text-base text-slate-700 mb-4 leading-relaxed">
            Your ShiftCover365 account is ready.
          </p>

          <ul className="space-y-2 mb-6">
            {[
              'Every ' + role + ' receives a 14-day free trial with full access.',
              'No payment details are required today.',
              'You’ll be able to choose the plan that suits your business later from Billing.',
            ].map((line, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
                <span>{line}</span>
              </li>
            ))}
          </ul>

          <Button
            onClick={handleContinue}
            disabled={busy}
            className="w-full h-12 rounded-full bg-[#0f1b3d] hover:bg-[#1a2d5a] text-white text-base font-semibold"
            data-testid="trial-ready-continue-btn"
          >
            Continue
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
